import { useSearch } from '@tanstack/react-router';
import { useEffect, useId, useMemo, useState, type FormEvent } from 'react';

import { Botao, BotaoDestrutivo } from '@/componentes/Botao';
import { CabecalhoPagina } from '@/componentes/CabecalhoPagina';
import { Campo, type OpcaoDeSelecao } from '@/componentes/Campo';
import { Barra, Carregando, ControleCarregando } from '@/componentes/Esqueleto';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { ListaDeEscolha } from '@/componentes/ListaDeEscolha';
import { Janela, JanelaDeAcao } from '@/componentes/JanelaDeAcao';
import { DadoChave, PainelDetalhe } from '@/componentes/PainelDetalhe';
import { RodapeTabela, Tabela, type Coluna } from '@/componentes/Tabela';
import { HistoriaNaMesa } from '@/historias/mesa';
import { formatarDecimal, formatarDia, formatarInteiro } from '@/lib/formato';
import { primeiraMaiuscula } from '@/lib/texto';
import { opcaoDaMaquina } from '@/modulos/equipamentos/opcao-da-maquina';
import { useOpcoesComVisualDaFrota } from '@/modulos/equipamentos/opcoesComVisual';
import { useSessao } from '@/modulos/sessao/consultas';

import {
  abrirArquivo,
  explicarFalha,
  useAlvos,
  useAplicar,
  useCompletarIndice,
  useDesaplicar,
  useDocumentos,
  useEnviarArquivo,
  useGravarDocumento,
  useIndice,
  lendoAgora,
  useLerPelaIa,
  useRevogarDocumento,
  useValidarDocumento,
} from './consultas';
import type { AplicacaoDaApi, DocumentoDaApi } from './api';

/**
 * Módulo 12 — a biblioteca técnica. Passo 2 da sequência do primeiro ciclo.
 *
 * ## O que esta tela existe para fazer
 *
 * Tudo do módulo 12 existia só como API: cadastrar, dizer a que o documento se
 * aplica, validar, revogar. Nenhuma pessoa alcançava nada disso. Esta é a tela
 * de quem cuida da biblioteca.
 *
 * ## A hierarquia de fontes é o que a tela precisa deixar óbvio
 *
 * O guia ordena seis níveis de fonte (§5.3), e o nível 1 é *"documento enviado
 * **e validado** pela empresa"*. A conjunção é carga: um procedimento da
 * empresa ainda pendente perde para o manual do fabricante, e passa a vencer no
 * instante em que alguém o valida.
 *
 * Quem cuida da biblioteca precisa ver isso sem precisar saber a regra. Por
 * isso a coluna **Vale como** escreve o nível por extenso, em vez de mostrar o
 * número que o banco calcula — "1º · empresa validado" diz o que o documento
 * significa; "1" não diz nada.
 *
 * ## O desenho (R01, p.27–29; p.19)
 *
 * A lista ocupa a largura inteira. O documento aberto vai para o PAINEL DE
 * DETALHE, irmão do conteúdo que rola: fixo ao lado de 1280 px para cima,
 * gaveta abaixo. Nele, a ordem fixa — identidade, dados-chave, abas (Arquivo, A
 * que se aplica, Situação) e no máximo duas ações: validar e revogar. Cadastrar
 * documento (sete campos) abre em janela, e revogar também: é decisão com
 * consequência, e pede o motivo.
 *
 * ## Uma única ação em laranja
 *
 * "Cadastrar documento", no cabeçalho — e, com a janela aberta, o de gravar
 * dela. Validar e revogar agem sobre um documento que já existe. Revogar usa o
 * botão destrutivo, que o manual manda nunca pintar de vermelho sólido.
 *
 * ## A recusa fica junto de quem a provocou (p.16)
 *
 * Cada parte guarda a própria recusa e a escreve embaixo do botão que falhou:
 * o arquivo na aba Arquivo, a ligação na aba A que se aplica, a validação na
 * nota das ações, o cadastro e a revogação nas janelas. Um alerta único no topo
 * da página ficava longe do painel — e, na gaveta, fora da vista.
 */

/* ---------------------------------------------------------------- o léxico */

/**
 * A hierarquia da §5.3, escrita.
 *
 * O banco calcula o número; esta tabela o traduz. O número sozinho é ruído para
 * quem está cadastrando — o que importa é "este vence aquele, e por quê".
 */
const NIVEL_DA_FONTE: Record<number, { ordem: string; porque: string }> = {
  1: { ordem: '1º', porque: 'da empresa, validado' },
  2: { ordem: '2º', porque: 'oficial do fabricante' },
  3: { ordem: '3º', porque: 'procedimento aprovado' },
  4: { ordem: '4º', porque: 'da empresa, aguardando validação' },
  5: { ordem: '5º', porque: 'fonte externa' },
  99: { ordem: '—', porque: 'revogado, fora das respostas' },
};

const ORIGEM: Record<string, string> = {
  empresa: 'Da empresa',
  fabricante: 'Do fabricante',
  procedimento: 'Procedimento aprovado',
  externo: 'Fonte externa',
};

const TIPO: Record<string, string> = {
  manual: 'Manual',
  catalogo: 'Catálogo',
  diagrama: 'Diagrama',
  boletim: 'Boletim',
  procedimento: 'Procedimento',
  outro: 'Outro',
};

const SITUACAO: Record<string, string> = {
  pendente: 'Aguardando validação',
  validado: 'Validado',
  recusado: 'Revogado',
};

const ALVO: Record<string, string> = {
  fabricante: 'Fabricante',
  modelo: 'Modelo',
  configuracao: 'Configuração',
  equipamento: 'Esta máquina',
};

/** O que o leitor de tela ouve enquanto as opções de "Qual" chegam. */
const CARREGANDO_O_ALVO = {
  fabricante: 'Carregando os fabricantes',
  modelo: 'Carregando os modelos',
  configuracao: 'Carregando as configurações',
  equipamento: 'Carregando os equipamentos',
} as const;

/**
 * A situação do documento em SELO PREENCHIDO (R02, p.31): tinta de fundo,
 * ponto na cor 500 e texto no tom escuro do mesmo tom — o texto colorido solto
 * media 3,9:1 no âmbar, abaixo dos 4,5:1 da regra 7. Sempre com o rótulo
 * escrito (regra 2).
 */
const SELO_DA_SITUACAO: Record<string, { readonly fundo: string; readonly ponto: string; readonly texto: string }> = {
  validado: { fundo: 'bg-operando-fundo', ponto: 'bg-operando', texto: 'text-operando-texto' },
  pendente: { fundo: 'bg-standby-fundo', ponto: 'bg-standby', texto: 'text-standby-texto' },
  recusado: { fundo: 'bg-parada-fundo', ponto: 'bg-parada', texto: 'text-parada-texto' },
};

function Situacao({ documento }: { readonly documento: DocumentoDaApi }) {
  const cores = SELO_DA_SITUACAO[documento.situacao] ?? SELO_DA_SITUACAO['pendente'];
  return (
    <span
      className={`inline-flex h-[var(--selo-altura)] items-center gap-2 rounded-pill px-2 ${cores?.fundo ?? ''} ${cores?.texto ?? ''}`}
    >
      <span aria-hidden="true" className={`size-[var(--selo-ponto)] shrink-0 rounded-full ${cores?.ponto ?? ''}`} />
      <span className="txt-selo">{SITUACAO[documento.situacao] ?? documento.situacao}</span>
    </span>
  );
}

const TIPO_DO_ARQUIVO: Readonly<Record<string, string>> = {
  'application/pdf': 'PDF',
  'image/png': 'Imagem PNG',
  'image/jpeg': 'Imagem JPEG',
};

type Recusa = { readonly erro: string; readonly comoResolver: string } | null;

const PADRAO_POR_PAGINA = 10;
const OPCOES_POR_PAGINA = [10, 25, 50, 100];

/**
 * "12,3 MB" com número e unidade separados (p.12): o número no tom do texto,
 * com dígitos de largura fixa, e a unidade em cinza. No tamanho da linha em
 * que aparece.
 */
function Megabytes({ bytes }: { readonly bytes: number }) {
  return (
    <span className="whitespace-nowrap">
      <span className="tabular-nums text-tinta">{formatarDecimal(bytes / (1024 * 1024), 1)}</span>
      <span className="text-tinta-secundaria"> MB</span>
    </span>
  );
}

/** "1 página", "12 páginas": a contagem flexiona o número (p.22–23). */
function contagem(quantidade: number, um: string, muitos: string): string {
  return `${formatarInteiro(quantidade)} ${quantidade === 1 ? um : muitos}`;
}

/** A recusa do servidor, escrita embaixo de quem a provocou: o que houve e o que fazer. */
function RecusaDaApi({ recusa }: { readonly recusa: Recusa }) {
  if (!recusa) return null;
  return (
    <div role="alert" className="bloco-rebaixado flex flex-col gap-1 p-3">
      <span className="txt-corpo text-tinta">{primeiraMaiuscula(recusa.erro)}</span>
      <span className="txt-apoio text-tinta-secundaria">{recusa.comoResolver}</span>
    </div>
  );
}

/**
 * O arquivo do documento: enviar, uma vez, e abrir.
 *
 * ESCOLHER E ENVIAR SÃO DOIS PASSOS, de propósito. O arquivo de um documento não
 * se troca depois de enviado — manual novo é revisão nova —, então enviar no
 * instante da escolha transformaria um clique no arquivo errado em definitivo.
 * A pessoa vê o nome e o tamanho antes de confirmar.
 */
function ArquivoDoDocumento({ documento }: { readonly documento: DocumentoDaApi }) {
  const base = useId();
  const enviar = useEnviarArquivo();
  /* Enviar o arquivo é de quem cuida da biblioteca (prova do marco, 07/10/2026). */
  const sessao = useSessao();
  const cuidaDaBiblioteca = (sessao.data?.permissoes ?? []).includes('documento_tecnico.gerir');
  const [escolhido, setEscolhido] = useState<File | null>(null);
  const [abrindo, setAbrindo] = useState(false);
  const [recusa, setRecusa] = useState<Recusa>(null);

  if (documento.temArquivo) {
    return (
      <div className="flex flex-col gap-3">
        <p className="txt-apoio text-tinta-secundaria">
          {TIPO_DO_ARQUIVO[documento.mime ?? ''] ?? 'Arquivo'}
          {documento.tamanhoBytes !== null ? (
            <>
              {' · '}
              <Megabytes bytes={documento.tamanhoBytes} />
            </>
          ) : null}
        </p>
        {/* O QUE A BUSCA ALCANÇA. Um manual escaneado e um com texto parecem
            iguais na lista; a diferença é que só um deles responde. */}
        {documento.temTexto ? (
          <p className="txt-apoio text-operando-texto">
            {`Texto extraído${documento.paginas ? ` de ${contagem(documento.paginas, 'página', 'páginas')}` : ''}: a busca encontra este documento e cita a página.`}
          </p>
        ) : null}
        {documento.temTexto && documento.textoPelaIa ? (
          <p className="txt-apoio text-tinta-secundaria">
            O texto foi lido pela IA a partir da imagem das páginas. A citação avisa isso, porque a transcrição pode ter erro
            de leitura: confira o valor no arquivo antes de agir.
          </p>
        ) : null}
        <LeituraPelaIa documento={documento} aoRecusar={setRecusa} />
        <div>
          <Botao
            nivel="terciario"
            disabled={abrindo}
            onClick={() => {
              setRecusa(null);
              setAbrindo(true);
              abrirArquivo(documento.id)
                .catch((falha: unknown) => {
                  setRecusa(explicarFalha(falha));
                })
                .finally(() => {
                  setAbrindo(false);
                });
            }}
          >
            {abrindo ? 'Abrindo…' : 'Abrir arquivo'}
          </Botao>
        </div>
        <RecusaDaApi recusa={recusa} />
      </div>
    );
  }

  if (documento.situacao === 'recusado') {
    return <p className="txt-apoio text-tinta-secundaria">Revogado sem arquivo enviado.</p>;
  }

  if (!cuidaDaBiblioteca) {
    return (
      <p className="txt-apoio text-standby-texto">
        Ainda sem arquivo. Sem ele, o documento está cadastrado mas não pode ser lido nem citado. Quem envia o arquivo é o
        perfil Administrador.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="txt-apoio text-standby-texto">
        Ainda sem arquivo. Sem ele, o documento está cadastrado mas não pode ser lido nem citado.
      </p>
      <Campo
        id={`${base}-arquivo`}
        rotulo="Arquivo do documento"
        ajuda="PDF do manual, ou a página escaneada em PNG ou JPEG, com até 100 MB. O tipo é conferido pelo conteúdo, e o arquivo não se troca depois de enviado."
      >
        <input
          id={`${base}-arquivo`}
          type="file"
          accept="application/pdf,image/png,image/jpeg"
          className="controle w-full"
          disabled={enviar.isPending}
          onChange={(e) => {
            setRecusa(null);
            setEscolhido(e.target.files?.[0] ?? null);
          }}
        />
      </Campo>
      {escolhido ? (
        <div className="flex flex-col gap-2">
          <p className="txt-apoio text-tinta-secundaria">
            {escolhido.name} · <Megabytes bytes={escolhido.size} />
          </p>
          <div>
            <Botao
              nivel="secundario"
              disabled={enviar.isPending}
              onClick={() => {
                enviar.mutate(
                  { id: documento.id, arquivo: escolhido },
                  {
                    onSuccess: () => {
                      setEscolhido(null);
                    },
                    onError: (falha) => {
                      setRecusa(explicarFalha(falha));
                    },
                  },
                );
              }}
            >
              {enviar.isPending ? 'Enviando…' : 'Enviar arquivo'}
            </Botao>
          </div>
        </div>
      ) : null}
      <RecusaDaApi recusa={recusa} />
    </div>
  );
}

/**
 * A LEITURA PELA IA (0034, DOC 01) — para o manual escaneado, que não tem
 * camada de texto. A IA lê as páginas no servidor, em segundo plano, e a lista
 * se atualiza sozinha enquanto ela lê. Documento restrito não vai para o
 * serviço de fora, e a tela diz isso em vez de oferecer o botão.
 */
function LeituraPelaIa({
  documento,
  aoRecusar,
}: {
  readonly documento: DocumentoDaApi;
  readonly aoRecusar: (recusa: Recusa) => void;
}) {
  const ler = useLerPelaIa();
  /* Pedir a leitura é de quem cuida da biblioteca: os outros leem a situação. */
  const sessao = useSessao();
  const cuidaDaBiblioteca = (sessao.data?.permissoes ?? []).includes('documento_tecnico.gerir');
  const semTextoDoPdf = !documento.temTexto || documento.textoPelaIa;
  if (!semTextoDoPdf && documento.leituraIa === null) return null;
  if (!documento.precisaOcr && documento.leituraIa === null) return null;

  if (lendoAgora(documento)) {
    return (
      <p role="status" className="txt-apoio text-tinta-secundaria">
        {`Lendo pela IA: ${formatarInteiro(documento.leituraIaPagina ?? 0)} de ${contagem(documento.paginas ?? 1, 'página', 'páginas')}. A lista se atualiza sozinha.`}
      </p>
    );
  }
  if (documento.restrito) {
    return (
      <p className="txt-apoio text-tinta-secundaria">
        Sem texto, e é restrito: a leitura pela IA manda o arquivo ao serviço de fora, e o restrito não viaja. A busca não
        alcança este documento.
      </p>
    );
  }
  /* "lendo" sem marca nova ha 20 minutos: a API reiniciou no meio, e a rota
     aceita o pedido de novo. */
  const parada = documento.leituraIa === 'lendo';
  const pedir = cuidaDaBiblioteca ? (
    <div>
      <Botao
        nivel="secundario"
        disabled={ler.isPending}
        onClick={() => {
          aoRecusar(null);
          ler.mutate(documento.id, {
            onError: (falha) => {
              aoRecusar(explicarFalha(falha));
            },
          });
        }}
      >
        {ler.isPending ? 'Pedindo…' : documento.leituraIa === 'falhou' || parada ? 'Ler de novo' : 'Ler pela IA'}
      </Botao>
    </div>
  ) : null;
  if (parada) {
    return (
      <div className="flex flex-col gap-2">
        <p role="alert" className="txt-apoio text-tinta">
          {`A leitura pela IA parou na página ${formatarInteiro(documento.leituraIaPagina ?? 0)} sem terminar, provavelmente porque o servidor reiniciou. Peça de novo; ela recomeça do início.`}
        </p>
        {pedir}
      </div>
    );
  }
  if (documento.leituraIa === 'falhou') {
    return (
      <div className="flex flex-col gap-2">
        <p role="alert" className="txt-apoio text-tinta">
          {`A leitura pela IA parou: ${documento.leituraIaErro ?? 'sem motivo registrado'}. Peça de novo; se repetir, confira se o arquivo abre e se as páginas estão legíveis.`}
        </p>
        {pedir}
      </div>
    );
  }
  if (documento.leituraIa === 'lida') return null;
  return (
    <div className="flex flex-col gap-2">
      <p className="txt-apoio text-tinta-secundaria">
        Sem texto: é imagem escaneada. A IA pode ler as páginas e transformar em texto com a página de cada trecho. Leva
        alguns minutos num manual grande, e tem custo por página.
      </p>
      {pedir}
    </div>
  );
}

/**
 * O ÍNDICE POR SENTIDO (0034): quantos trechos a pergunta à IA acha também
 * pelo sentido, e não só pela palavra. Ele se completa sozinho quando entra
 * texto novo; o botão é para o que ficou para trás.
 */
function IndicePorSentido() {
  const indice = useIndice();
  const completar = useCompletarIndice();
  const sessao = useSessao();
  const cuidaDaBiblioteca = (sessao.data?.permissoes ?? []).includes('documento_tecnico.gerir');
  const i = indice.data;
  /* Carregando (p.32) na forma da linha que vai chegar, e não a frase solta. */
  if (indice.isPending) {
    return (
      <Carregando oQue="Carregando a busca por sentido">
        <Barra className="my-1 h-3 w-3/4" />
      </Carregando>
    );
  }
  if (indice.isError) {
    return (
      <p role="alert" className="txt-apoio text-tinta">
        {`Não foi possível conferir a busca por sentido: ${explicarFalha(indice.error).comoResolver}`}
      </p>
    );
  }
  if (!i) return null;
  if (!i.ligado) {
    /* O AVISO É DE QUEM CUIDA DA BIBLIOTECA, e diz o que fazer (p.22–23):
       para quem só consulta, "busca por sentido desligada" é jargão de
       software sem ação nenhuma ao alcance. */
    if (!cuidaDaBiblioteca) return null;
    return (
      <p className="txt-apoio text-pretty text-tinta-secundaria">
        A pergunta à IA acha só os trechos que usam as mesmas palavras dela. Para achar também os que dizem o mesmo
        com outras palavras, peça a quem administra a plataforma para ligar a busca por sentido em Configurações do
        sistema.
      </p>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-3">
      <p className="txt-apoio text-tinta-secundaria">
        {'Busca por sentido: '}
        <span className="tabular-nums text-tinta">{formatarInteiro(i.comVetor)}</span>
        {` de ${contagem(i.trechos, 'trecho', 'trechos')} — a pergunta à IA acha pelo sentido, e não só pela palavra.`}
        {i.trechosRestritos > 0
          ? ` ${contagem(i.trechosRestritos, 'trecho de documento restrito fica', 'trechos de documento restrito ficam')} de fora, porque não viajam.`
          : ''}
      </p>
      {cuidaDaBiblioteca && i.pendentes > 0 ? (
        <Botao
          nivel="terciario"
          disabled={completar.isPending}
          onClick={() => {
            completar.mutate();
          }}
        >
          {completar.isPending ? 'Pedindo…' : `Completar ${contagem(i.pendentes, 'pendente', 'pendentes')}`}
        </Botao>
      ) : null}
      {completar.isError ? (
        <p role="alert" className="txt-apoio text-tinta">{`${primeiraMaiuscula(explicarFalha(completar.error).erro)}. ${explicarFalha(completar.error).comoResolver}`}</p>
      ) : null}
    </div>
  );
}

/** O que o documento alcança, em uma frase por linha. */
function Alcance({ aplicacoes }: { readonly aplicacoes: readonly AplicacaoDaApi[] }) {
  if (aplicacoes.length === 0) {
    /* Documento sem aplicação EXISTE e não responde nada. Dizer isso é o que
       impede alguém de cadastrar dez manuais e não entender por que a busca
       continua vazia. */
    return <span className="txt-apoio text-standby-texto">Não alcança máquina nenhuma</span>;
  }
  return (
    <span className="flex flex-col gap-1">
      {aplicacoes.map((a) => (
        <span key={a.id} className="txt-corpo">
          {ALVO[a.alvo] ?? a.alvo}
          {a.nome ? ` · ${a.nome}` : ''}
          {a.serieDe || a.serieAte ? (
            <span className="txt-apoio text-tinta-apagada">
              {` · série ${a.serieDe ?? '…'}–${a.serieAte ?? '…'}`}
            </span>
          ) : null}
        </span>
      ))}
    </span>
  );
}

export function PaginaBiblioteca() {
  const documentos = useDocumentos();
  /* Cadastrar documento é de quem cuida da biblioteca (prova do marco,
     07/10/2026): quem consulta vê a lista e o painel, sem o botão. */
  const sessao = useSessao();
  const cuidaDaBiblioteca = (sessao.data?.permissoes ?? []).includes('documento_tecnico.gerir');

  const [cadastrando, setCadastrando] = useState(false);
  /* O documento achado pela busca da barra superior chega na URL. */
  const { documento: pedido } = useSearch({ from: '/tecnico' });
  const [selecionado, setSelecionado] = useState<string | null>(pedido ?? null);
  useEffect(() => {
    if (pedido) setSelecionado(pedido);
  }, [pedido]);
  /* A lista cresce sem teto: rodapé com contagem e páginas (p.18), como as
     outras tabelas da oficina e do estoque. */
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(PADRAO_POR_PAGINA);

  const aberto = useMemo(
    () => (documentos.data ?? []).find((d) => d.id === selecionado) ?? null,
    [documentos.data, selecionado],
  );

  const todos = documentos.data ?? [];
  const paginas = Math.max(Math.ceil(todos.length / porPagina), 1);
  const paginaSegura = Math.min(pagina, paginas);
  const inicio = (paginaSegura - 1) * porPagina;
  const visiveis = todos.slice(inicio, inicio + porPagina);

  const colunas: readonly Coluna<DocumentoDaApi>[] = [
    {
      chave: 'titulo',
      rotulo: 'Documento',
      /* O título vai a até duas linhas, quebrando entre palavras (p.18); o
         inteiro fica no `title` e no painel. Tipo, revisão e data, embaixo,
         não partem. */
      celula: (d) => (
        <span className="flex flex-col gap-1">
          <span className="line-clamp-2 min-w-48 whitespace-normal text-pretty" title={d.titulo}>
            {d.titulo}
          </span>
          <span className="txt-apoio whitespace-nowrap text-tinta-apagada">
            {TIPO[d.tipo] ?? d.tipo}
            {d.revisao ? ` · rev. ${d.revisao}` : ''}
            {d.publicadoEm ? ` · ${formatarDia(d.publicadoEm)}` : ''}
          </span>
        </span>
      ),
    },
    {
      chave: 'origem',
      rotulo: 'Origem',
      /* Secundária (R01, p.28): sai abaixo de 1280 px. "Vale como" já diz de
         onde vem a força do documento, e o painel mostra a origem. */
      prioridade: 'secundaria',
      celula: (d) => ORIGEM[d.origem] ?? d.origem,
    },
    {
      chave: 'prioridadeFonte',
      rotulo: 'Vale como',
      celula: (d) => {
        const nivel = NIVEL_DA_FONTE[d.prioridadeFonte];
        return (
          /* Um alinhamento por coluna (p.18): o ordinal fica à esquerda, em
             cima da frase que o explica — só com os dígitos de largura fixa. */
          <span className="flex flex-col gap-1">
            <span className="tabular-nums">{nivel?.ordem ?? '—'}</span>
            <span className="txt-apoio text-tinta-apagada">{nivel?.porque ?? ''}</span>
          </span>
        );
      },
    },
    { chave: 'situacao', rotulo: 'Situação', alinhamento: 'status', celula: (d) => <Situacao documento={d} /> },
    { chave: 'alcance', rotulo: 'Alcança', celula: (d) => <Alcance aplicacoes={d.aplicacoes} /> },
    {
      chave: 'paginas',
      rotulo: 'Páginas',
      alinhamento: 'numero',
      /* Secundária (R01, p.28): continua nos dados-chave do painel. */
      prioridade: 'secundaria',
      celula: (d) => (d.paginas === null ? '—' : formatarInteiro(d.paginas)),
    },
  ];

  return (
    <>
      <div className="min-w-0 flex-1 overflow-auto">
        <div className="pagina">
          <CabecalhoPagina
            /* A palavra da obra, e não a numeração interna do mapa v6 (p.22):
               as outras telas dizem "Manutenção", "Oficina", "Almoxarifado". */
            sobretitulo="Biblioteca técnica"
            titulo="Manuais e documentos"
            subtitulo="Manual, catálogo, boletim e procedimento, ligados ao equipamento a que se aplicam. É daqui que sai a resposta técnica com a página citada."
            acoes={
              cuidaDaBiblioteca ? (
                <HistoriaNaMesa codigo="BTN-BIB-01" className="flex">
                  <Botao
                    nivel="primario"
                    onClick={() => {
                      setCadastrando(true);
                    }}
                  >
                    Cadastrar documento
                  </Botao>
                </HistoriaNaMesa>
              ) : undefined
            }
          />

          <IndicePorSentido />

          <div data-painel-origem>
            <Tabela
              rotulo="Documentos da biblioteca"
              colunas={colunas}
              linhas={visiveis}
              alturaDupla
              chaveDaLinha={(d) => d.id}
              carregando={documentos.isPending}
              selecionada={selecionado}
              aoSelecionar={(d) => {
                setSelecionado(d.id);
              }}
              erro={
                documentos.isError ? (
                  <EstadoVazio
                    titulo="Não foi possível carregar a biblioteca"
                    descricao={explicarFalha(documentos.error).comoResolver}
                    acao={
                      <Botao
                        nivel="secundario"
                        onClick={() => {
                          void documentos.refetch();
                        }}
                      >
                        Carregar a biblioteca
                      </Botao>
                    }
                  />
                ) : null
              }
              vazio={
                <EstadoVazio
                  titulo="Nenhum documento na biblioteca"
                  descricao={
                    cuidaDaBiblioteca
                      ? 'Cadastre o manual do primeiro equipamento. Depois de ligá-lo ao modelo, a resposta técnica passa a citar documento e página.'
                      : 'Quem cadastra os manuais é o perfil Administrador. Eles aparecem aqui, e a resposta técnica passa a citar documento e página.'
                  }
                  acao={
                    cuidaDaBiblioteca ? (
                      <Botao
                        nivel="secundario"
                        onClick={() => {
                          setCadastrando(true);
                        }}
                      >
                        Cadastrar documento
                      </Botao>
                    ) : undefined
                  }
                />
              }
              rodape={
                <RodapeTabela
                  primeiro={inicio + 1}
                  ultimo={inicio + visiveis.length}
                  total={todos.length}
                  pagina={paginaSegura}
                  paginas={paginas}
                  porPagina={porPagina}
                  opcoesPorPagina={OPCOES_POR_PAGINA}
                  aoMudarPagina={setPagina}
                  aoMudarPorPagina={(novo) => {
                    setPorPagina(novo);
                    setPagina(1);
                  }}
                />
              }
            />
          </div>
        </div>
      </div>

      {/* Com a janela de cadastro aberta, o painel sai: gaveta e janela
          empilhadas a R01 proíbe. */}
      {aberto && !cadastrando ? (
        <PainelDoDocumento
          key={aberto.id}
          documento={aberto}
          aoFechar={() => {
            setSelecionado(null);
          }}
        />
      ) : null}

      <Janela
        titulo="Cadastrar documento"
        aberta={cadastrando && cuidaDaBiblioteca}
        aoFechar={() => {
          setCadastrando(false);
        }}
      >
        <CadastroDeDocumento
          aoCancelar={() => {
            setCadastrando(false);
          }}
          aoCadastrar={(id) => {
            setCadastrando(false);
            setSelecionado(id);
          }}
        />
      </Janela>
    </>
  );
}

/**
 * O cadastro do documento, em janela (R01, p.29: sete campos, tarefa curta).
 *
 * Título e restrição são conferidos AO SAIR do campo (p.16), com o erro
 * embaixo dele. A conferência do envio continua como rede, e leva o foco ao
 * primeiro campo que falta.
 */
function CadastroDeDocumento({
  aoCancelar,
  aoCadastrar,
}: {
  readonly aoCancelar: () => void;
  readonly aoCadastrar: (id: string) => void;
}) {
  const base = useId();
  const gravar = useGravarDocumento();

  const [titulo, setTitulo] = useState('');
  const [tipo, setTipo] = useState('manual');
  const [origem, setOrigem] = useState('fabricante');
  const [revisao, setRevisao] = useState('');
  const [paginas, setPaginas] = useState('');
  const [restrito, setRestrito] = useState(false);
  const [restricaoNota, setRestricaoNota] = useState('');
  const [erros, setErros] = useState<Record<string, string>>({});
  const [recusa, setRecusa] = useState<Recusa>(null);

  const FALTA_TITULO = 'Informe o título do documento.';
  const FALTA_RESTRICAO = 'Diga qual é a restrição — marca sem explicação ninguém sabe honrar.';

  function marcar(campo: string, mensagem: string | null) {
    setErros((atual) => {
      const resto = Object.fromEntries(Object.entries(atual).filter(([chave]) => chave !== campo));
      return mensagem ? { ...resto, [campo]: mensagem } : resto;
    });
  }

  function aoEnviar(evento: FormEvent) {
    evento.preventDefault();
    const faltando: Record<string, string> = {};
    if (titulo.trim() === '') faltando['titulo'] = FALTA_TITULO;
    if (restrito && restricaoNota.trim() === '') faltando['restricaoNota'] = FALTA_RESTRICAO;
    if (Object.keys(faltando).length > 0) {
      setErros(faltando);
      document.getElementById(`${base}-${faltando['titulo'] ? 'titulo' : 'restricao'}`)?.focus();
      return;
    }
    setErros({});
    setRecusa(null);

    gravar.mutate(
      {
        envio: {
          titulo: titulo.trim(),
          tipo,
          origem,
          ...(revisao.trim() === '' ? {} : { revisao: revisao.trim() }),
          ...(paginas.trim() === '' ? {} : { paginas: Number(paginas) }),
          ...(restrito ? { restrito: true, restricaoNota: restricaoNota.trim() } : {}),
        },
      },
      {
        onSuccess: (novo) => {
          aoCadastrar((novo as DocumentoDaApi).id);
        },
        onError: (falha) => {
          setRecusa(explicarFalha(falha));
        },
      },
    );
  }

  const marcaDeErro = (campo: string, id: string) =>
    erros[campo] ? { 'aria-invalid': true, 'aria-describedby': `${id}-erro` } : {};

  return (
    <form noValidate onSubmit={aoEnviar} className="formulario-grade">
      <div className="campo-largo min-w-0">
        <Campo id={`${base}-titulo`} rotulo="Título" erro={erros['titulo'] ?? null}>
          <input
            id={`${base}-titulo`}
            type="text"
            autoComplete="off"
            className="controle w-full"
            placeholder="Manual de operação 320 GC"
            value={titulo}
            onChange={(e) => {
              marcar('titulo', null);
              setTitulo(e.target.value);
            }}
            onBlur={(e) => {
              marcar('titulo', e.target.value.trim() === '' ? FALTA_TITULO : null);
            }}
            {...marcaDeErro('titulo', `${base}-titulo`)}
          />
        </Campo>
      </div>

      <Campo id={`${base}-tipo`} rotulo="Tipo">
        <select
          id={`${base}-tipo`}
          className="controle w-full"
          value={tipo}
          onChange={(e) => {
            setTipo(e.target.value);
          }}
        >
          {Object.entries(TIPO).map(([valor, rotulo]) => (
            <option key={valor} value={valor}>
              {rotulo}
            </option>
          ))}
        </select>
      </Campo>

      <Campo
        id={`${base}-origem`}
        rotulo="Origem"
        ajuda="Decide a força do documento: o da empresa, depois de validado, vence o do fabricante."
      >
        <select
          id={`${base}-origem`}
          className="controle w-full"
          value={origem}
          onChange={(e) => {
            setOrigem(e.target.value);
          }}
        >
          {Object.entries(ORIGEM).map(([valor, rotulo]) => (
            <option key={valor} value={valor}>
              {rotulo}
            </option>
          ))}
        </select>
      </Campo>

      <Campo id={`${base}-revisao`} rotulo="Revisão" opcional>
        <input
          id={`${base}-revisao`}
          type="text"
          autoComplete="off"
          className="controle w-full"
          placeholder="Rev. C"
          value={revisao}
          onChange={(e) => {
            setRevisao(e.target.value);
          }}
        />
      </Campo>

      <Campo id={`${base}-paginas`} rotulo="Páginas" opcional>
        <input
          id={`${base}-paginas`}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          className="controle w-full"
          value={paginas}
          onChange={(e) => {
            setPaginas(e.target.value);
          }}
        />
      </Campo>

      <label className="campo-linha flex items-start gap-2">
        <input
          type="checkbox"
          className="mt-1"
          checked={restrito}
          onChange={(e) => {
            setRestrito(e.target.checked);
            if (!e.target.checked) marcar('restricaoNota', null);
          }}
        />
        <span className="txt-apoio text-tinta-secundaria">
          Tem restrição de uso. Manual de fabricante costuma proibir redistribuição.
        </span>
      </label>

      {restrito ? (
        <div className="campo-largo min-w-0">
          <Campo id={`${base}-restricao`} rotulo="Qual é a restrição" erro={erros['restricaoNota'] ?? null}>
            <input
              id={`${base}-restricao`}
              type="text"
              autoComplete="off"
              className="controle w-full"
              placeholder="Uso interno, sem redistribuição"
              value={restricaoNota}
              onChange={(e) => {
                marcar('restricaoNota', null);
                setRestricaoNota(e.target.value);
              }}
              onBlur={(e) => {
                marcar('restricaoNota', e.target.value.trim() === '' ? FALTA_RESTRICAO : null);
              }}
              {...marcaDeErro('restricaoNota', `${base}-restricao`)}
            />
          </Campo>
        </div>
      ) : null}

      {recusa ? (
        <div className="campo-linha">
          <RecusaDaApi recusa={recusa} />
        </div>
      ) : null}

      {/* Barra de ações (R01): o de sair à esquerda, como TERCIÁRIO e com
          objeto no rótulo (p.15, p.29); o primário à direita. */}
      <div className="barra-de-acoes campo-linha">
        <Botao nivel="terciario" type="button" onClick={aoCancelar}>
          Descartar cadastro
        </Botao>
        <Botao nivel="primario" type="submit" disabled={gravar.isPending}>
          {gravar.isPending ? 'Cadastrando…' : 'Cadastrar documento'}
        </Botao>
      </div>
    </form>
  );
}

/**
 * O documento aberto, no painel de detalhe (p.19): identidade, dados-chave,
 * abas e no máximo duas ações — validar e revogar.
 */
function PainelDoDocumento({ documento, aoFechar }: { readonly documento: DocumentoDaApi; readonly aoFechar: () => void }) {
  const validar = useValidarDocumento();
  const [recusa, setRecusa] = useState<Recusa>(null);
  const [revogando, setRevogando] = useState(false);
  /* Validar e revogar, cada um com a sua licença (prova do marco, 07/10/2026). */
  const sessao = useSessao();
  const podeValidar = (sessao.data?.permissoes ?? []).includes('documento_tecnico.validar');
  const podeRevogar = (sessao.data?.permissoes ?? []).includes('documento_tecnico.revogar');
  const nivel = NIVEL_DA_FONTE[documento.prioridadeFonte];

  const acoes = [
    podeValidar && documento.situacao === 'pendente' ? (
      <Botao
        key="validar"
        nivel="secundario"
        disabled={validar.isPending}
        onClick={() => {
          setRecusa(null);
          validar.mutate(
            { id: documento.id, motivo: 'conferido por quem cuida da biblioteca' },
            {
              onError: (f) => {
                setRecusa(explicarFalha(f));
              },
            },
          );
        }}
      >
        Validar documento
      </Botao>
    ) : null,
    !podeRevogar || documento.situacao === 'recusado' ? null : (
      <HistoriaNaMesa key="revogar" codigo="BTN-BIB-03" className="flex">
        <JanelaDeAcao
          rotulo="Revogar documento"
          titulo="Revogar documento"
          nivel="destrutivo"
          aberta={revogando}
          aoAbrir={() => {
            setRevogando(true);
          }}
          aoFechar={() => {
            setRevogando(false);
          }}
        >
          <Revogacao
            documento={documento}
            aoConcluir={() => {
              setRevogando(false);
            }}
          />
        </JanelaDeAcao>
      </HistoriaNaMesa>
    ),
  ].filter((acao) => acao !== null);

  return (
    <PainelDetalhe
      titulo={documento.titulo}
      subtitulo={`${TIPO[documento.tipo] ?? documento.tipo} · ${ORIGEM[documento.origem] ?? documento.origem}${documento.revisao ? ` · rev. ${documento.revisao}` : ''}`}
      marcadores={
        <>
          <Situacao documento={documento} />
          {documento.restrito ? (
            <span className="inline-flex h-[var(--selo-altura)] items-center rounded-badge border border-borda px-2">
              <span className="txt-badge text-tinta">Restrito</span>
            </span>
          ) : null}
        </>
      }
      dadosChave={
        <>
          <DadoChave rotulo="Vale como">{nivel ? `${nivel.ordem} · ${nivel.porque}` : '—'}</DadoChave>
          <DadoChave rotulo="Alcança">
            {documento.aplicacoes.length === 0 ? (
              <span className="text-standby-texto">Nenhuma máquina</span>
            ) : (
              contagem(documento.aplicacoes.length, 'ligação', 'ligações')
            )}
          </DadoChave>
          <DadoChave rotulo="Páginas">
            <span className="tabular-nums">{documento.paginas === null ? '—' : formatarInteiro(documento.paginas)}</span>
          </DadoChave>
          {documento.publicadoEm ? (
            <DadoChave rotulo="Publicado em">{formatarDia(documento.publicadoEm)}</DadoChave>
          ) : null}
        </>
      }
      abas={[
        { id: 'arquivo', rotulo: 'Arquivo', conteudo: <ArquivoDoDocumento documento={documento} /> },
        { id: 'aplica', rotulo: 'A que se aplica', conteudo: <AplicacoesDoDocumento documento={documento} /> },
        { id: 'situacao', rotulo: 'Situação', conteudo: <SituacaoDoDocumento documento={documento} /> },
      ]}
      acoes={acoes}
      {...(recusa ? { notaDasAcoes: `${primeiraMaiuscula(recusa.erro)}. ${recusa.comoResolver}` } : {})}
      aoFechar={aoFechar}
    />
  );
}

function SituacaoDoDocumento({ documento }: { readonly documento: DocumentoDaApi }) {
  return (
    <div className="flex flex-col gap-3">
      <p className="txt-apoio text-tinta-secundaria">
        {documento.situacao === 'pendente'
          ? 'Enquanto não for validado, este documento vale menos que o do fabricante nas respostas técnicas. Valide depois de conferir o conteúdo.'
          : documento.situacao === 'recusado'
            ? 'Revogado, e fora das respostas técnicas. Ele continua aqui para explicar respostas que já foram dadas.'
            : 'Validado: entra nas respostas técnicas com a força escrita em Vale como.'}
      </p>
      {documento.restrito ? (
        <p className="txt-apoio rounded-controle bg-standby-fundo p-3 text-standby-texto">
          {`Restrito: ${documento.restricaoNota ?? 'sem a restrição escrita'}`}
        </p>
      ) : null}
    </div>
  );
}

/**
 * A revogação pede o motivo NA JANELA, e não num alerta do navegador.
 *
 * `window.prompt` não obedece regra nenhuma do manual: não tem o anel de foco
 * do sistema, não põe o erro abaixo do campo, não usa a tipografia da marca,
 * e alguns navegadores simplesmente o bloqueiam.
 */
function Revogacao({ documento, aoConcluir }: { readonly documento: DocumentoDaApi; readonly aoConcluir: () => void }) {
  const base = useId();
  const revogar = useRevogarDocumento();
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [recusa, setRecusa] = useState<Recusa>(null);
  const FALTA_MOTIVO = 'Diga por que está revogando — quem vier depois precisa saber.';

  function aoEnviar(evento: FormEvent) {
    evento.preventDefault();
    if (motivo.trim().length < 3) {
      setErro(FALTA_MOTIVO);
      document.getElementById(`${base}-motivo`)?.focus();
      return;
    }
    setErro(null);
    setRecusa(null);
    revogar.mutate(
      { id: documento.id, motivo: motivo.trim() },
      {
        onSuccess: aoConcluir,
        onError: (falha) => {
          setRecusa(explicarFalha(falha));
        },
      },
    );
  }

  return (
    <form noValidate onSubmit={aoEnviar} className="formulario-grade">
      {/* A confirmação diz a CONSEQUÊNCIA, e não pergunta "tem certeza" — é o
          que o manual manda. */}
      <div className="campo-linha flex flex-col gap-2">
        <p className="txt-subtitulo text-tinta">{`Revogar ${documento.titulo}?`}</p>
        <p className="txt-corpo text-tinta-secundaria">
          Ele sai das respostas técnicas imediatamente, e continua na biblioteca para explicar respostas que já foram
          dadas.
        </p>
      </div>
      <div className="campo-linha">
        <Campo id={`${base}-motivo`} rotulo="Por quê" erro={erro}>
          <input
            id={`${base}-motivo`}
            type="text"
            autoComplete="off"
            className="controle w-full"
            placeholder="Superado pela revisão D"
            value={motivo}
            onChange={(e) => {
              setErro(null);
              setMotivo(e.target.value);
            }}
            onBlur={(e) => {
              setErro(e.target.value.trim().length < 3 ? FALTA_MOTIVO : null);
            }}
            {...(erro ? { 'aria-invalid': true, 'aria-describedby': `${base}-motivo-erro` } : {})}
          />
        </Campo>
      </div>
      {recusa ? (
        <div className="campo-linha">
          <RecusaDaApi recusa={recusa} />
        </div>
      ) : null}
      {/* Barra de ações (R01): o de sair à esquerda, como TERCIÁRIO (p.15,
          p.29); a ação à direita. */}
      <div className="barra-de-acoes campo-linha">
        <Botao nivel="terciario" type="button" onClick={aoConcluir}>
          Manter documento
        </Botao>
        <Botao nivel="destrutivo" type="submit" disabled={revogar.isPending}>
          {revogar.isPending ? 'Revogando…' : 'Revogar documento'}
        </Botao>
      </div>
    </form>
  );
}

/** A que o documento se aplica: as ligações que ele tem, e o formulário de ligar mais uma. */
function AplicacoesDoDocumento({ documento }: { readonly documento: DocumentoDaApi }) {
  const base = useId();
  const alvos = useAlvos();
  const comVisualDaFrota = useOpcoesComVisualDaFrota();
  const aplicar = useAplicar();
  const desaplicar = useDesaplicar();
  /* Ligar e desligar o documento é de quem cuida da biblioteca. */
  const sessao = useSessao();
  const cuidaDaBiblioteca = (sessao.data?.permissoes ?? []).includes('documento_tecnico.gerir');
  const [recusa, setRecusa] = useState<Recusa>(null);
  const [erroDoAlvo, setErroDoAlvo] = useState<string | null>(null);

  /* O alvo da aplicabilidade: um só, e o banco também cobra. */
  const [alvoTipo, setAlvoTipo] = useState<'fabricante' | 'modelo' | 'configuracao' | 'equipamento'>('modelo');
  const [alvoId, setAlvoId] = useState('');
  const [serieDe, setSerieDe] = useState('');
  const [serieAte, setSerieAte] = useState('');

  const opcoesDoAlvo = useMemo((): readonly OpcaoDeSelecao[] => {
    const a = alvos.data;
    if (!a) return [];
    if (alvoTipo === 'fabricante') return a.fabricantes.map((x) => ({ valor: x.id, rotulo: x.nome }));
    if (alvoTipo === 'modelo') return a.modelos.map((x) => ({ valor: x.id, rotulo: `${x.fabricante} · ${x.nome}` }));
    if (alvoTipo === 'configuracao') {
      return a.configuracoes.map((x) => ({ valor: x.id, rotulo: `${x.modelo} · ${x.nome}` }));
    }
    /* A máquina com o código interno e a placa em etiqueta (31/10/2026). */
    return comVisualDaFrota(a.equipamentos.map((x) => opcaoDaMaquina(x)));
  }, [alvos.data, alvoTipo, comVisualDaFrota]);

  const FALTA_ALVO = 'Escolha a que este documento se aplica.';
  const revogado = documento.situacao === 'recusado';

  function aoAplicar(evento: FormEvent) {
    evento.preventDefault();
    if (alvoId === '') {
      setErroDoAlvo(FALTA_ALVO);
      document.getElementById(`${base}-alvoId`)?.focus();
      return;
    }
    setErroDoAlvo(null);
    setRecusa(null);

    const chave = `${alvoTipo}Id` as const;
    aplicar.mutate(
      {
        id: documento.id,
        envio: {
          [chave]: alvoId,
          ...(serieDe.trim() === '' ? {} : { serieDe: serieDe.trim() }),
          ...(serieAte.trim() === '' ? {} : { serieAte: serieAte.trim() }),
        },
      },
      {
        onSuccess: () => {
          setAlvoId('');
          setSerieDe('');
          setSerieAte('');
        },
        onError: (falha) => {
          setRecusa(explicarFalha(falha));
        },
      },
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {documento.aplicacoes.length > 0 ? (
        <ul className="flex flex-col gap-2">
          {documento.aplicacoes.map((a) => {
            const alvo = `${ALVO[a.alvo] ?? a.alvo}${a.nome ? ` · ${a.nome}` : ''}`;
            return (
              <li key={a.id} className="group flex items-start justify-between gap-2 border-b border-borda-sutil pb-2">
                <span className="txt-corpo">{alvo}</span>
                {revogado || !cuidaDaBiblioteca ? null : (
                  /* A ação da linha aparece no hover ou no foco (p.18), e
                     sempre com confirmação (p.15). Em tela de toque, sempre
                     visível. */
                  <span className="inline-flex shrink-0 text-left group-focus-within:opacity-100 group-hover:opacity-100 [@media(hover:hover)]:opacity-0">
                    <BotaoDestrutivo
                      rotulo="Desligar aplicação"
                      pergunta={`Desligar ${documento.titulo} de ${alvo}?`}
                      consequencia="A resposta técnica deixa de citar este documento para esse alvo. Dá para ligar de novo depois."
                      rotuloConfirmar="Desligar aplicação"
                      rotuloCancelar="Manter ligado"
                      {...(desaplicar.isPending ? { motivoIndisponivel: 'Espere o pedido anterior terminar.' } : {})}
                      aoConfirmar={() => {
                        setRecusa(null);
                        desaplicar.mutate(
                          { documentoId: documento.id, aplicacaoId: a.id },
                          {
                            onError: (f) => {
                              setRecusa(explicarFalha(f));
                            },
                          },
                        );
                      }}
                    />
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="txt-apoio text-standby-texto">
          {revogado || !cuidaDaBiblioteca
            ? 'Não alcança máquina nenhuma.'
            : 'Ainda não alcança máquina nenhuma. Ligue-o a um fabricante, modelo, configuração ou equipamento.'}
        </p>
      )}

      {revogado ? (
        <p className="txt-apoio text-tinta-secundaria">Revogado: não se liga a mais nada.</p>
      ) : !cuidaDaBiblioteca ? null : (
        <form noValidate onSubmit={aoAplicar} className="flex flex-col gap-4 border-t border-borda-sutil pt-4">
          <HistoriaNaMesa codigo="CMP-BIB-02" className="min-w-0">
            <Campo id={`${base}-alvoTipo`} rotulo="Ligar a">
              <select
                id={`${base}-alvoTipo`}
                className="controle w-full"
                value={alvoTipo}
                onChange={(e) => {
                  setAlvoTipo(e.target.value as typeof alvoTipo);
                  setAlvoId('');
                }}
              >
                <option value="fabricante">Fabricante</option>
                <option value="modelo">Modelo</option>
                <option value="configuracao">Configuração</option>
                <option value="equipamento">Um equipamento</option>
              </select>
            </Campo>
          </HistoriaNaMesa>

          <Campo id={`${base}-alvoId`} rotulo="Qual" erro={erroDoAlvo}>
            {/* As opções ainda vêm (p.32): a lista só com "Escolha" deixava
                escolher o nada. */}
            {alvos.isPending ? (
              <ControleCarregando oQue={CARREGANDO_O_ALVO[alvoTipo]} />
            ) : (
              <ListaDeEscolha
                id={`${base}-alvoId`}
                rotulo="Qual"
                valor={alvoId}
                aoMudar={(valor) => {
                  setErroDoAlvo(null);
                  setAlvoId(valor);
                }}
                rotuloVazio="Escolha"
                opcoes={opcoesDoAlvo}
                idDoErro={erroDoAlvo ? `${base}-alvoId-erro` : undefined}
                /* O leitor só quando o documento é de uma máquina: fabricante, modelo e configuração não têm adesivo. */
                adesivo={alvoTipo === 'equipamento'}
              />
            )}
          </Campo>

          <div className="grid grid-cols-2 gap-4">
            <Campo id={`${base}-serieDe`} rotulo="Série de" opcional>
              <input
                id={`${base}-serieDe`}
                type="text"
                autoComplete="off"
                className="controle w-full"
                value={serieDe}
                onChange={(e) => {
                  setSerieDe(e.target.value);
                }}
              />
            </Campo>
            <Campo
              id={`${base}-serieAte`}
              rotulo="Série até"
              opcional
              ajuda="Deixe os dois vazios para valer para todo o alvo."
            >
              <input
                id={`${base}-serieAte`}
                type="text"
                autoComplete="off"
                className="controle w-full"
                value={serieAte}
                onChange={(e) => {
                  setSerieAte(e.target.value);
                }}
              />
            </Campo>
          </div>

          <div className="flex justify-end">
            <Botao nivel="secundario" type="submit" disabled={aplicar.isPending}>
              {aplicar.isPending ? 'Ligando…' : 'Ligar documento'}
            </Botao>
          </div>
        </form>
      )}

      <RecusaDaApi recusa={recusa} />
    </div>
  );
}
